import { dataApp } from '../config/firebase';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from 'firebase/functions';
import { SOPSummary, SOPSummaryStats, SOPWithSummary, SOP_SUMMARIES_COLLECTION } from '../types/sopSummary';
import { SOP } from './firebase/SOPService';
import { firestoreWrapper } from './firestoreWrapper';

/**
 * Service for managing SOP summaries
 */
class SOPSummaryService {
  private static instance: SOPSummaryService;
  private db = getFirestore(dataApp);
  private functions = getFunctions(dataApp, 'us-east1');

  private constructor() {}

  static getInstance(): SOPSummaryService {
    if (!SOPSummaryService.instance) {
      SOPSummaryService.instance = new SOPSummaryService();
    }
    return SOPSummaryService.instance;
  }

  /**
   * Get a SOP summary by ID
   * @param id SOP ID
   * @returns SOP summary or null if not found
   */
  async getSummary(id: string): Promise<SOPSummary | null> {
    try {
      const docRef = doc(this.db, SOP_SUMMARIES_COLLECTION, id);
      const docSnap = await firestoreWrapper.getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }
      
      return docSnap.data() as SOPSummary;
    } catch (error) {
      console.error(`Error getting SOP summary for ID ${id}:`, error);
      throw new Error('Failed to get SOP summary');
    }
  }

  /**
   * Get all SOP summaries
   * @returns Array of SOP summaries
   */
  async getAllSummaries(): Promise<SOPSummary[]> {
    try {
      const summariesCollection = collection(this.db, SOP_SUMMARIES_COLLECTION);
      const snapshot = await firestoreWrapper.getDocs(summariesCollection);
      return snapshot.docs.map(doc => doc.data() as SOPSummary);
    } catch (error) {
      console.error('Error getting all SOP summaries:', error);
      throw new Error('Failed to get SOP summaries');
    }
  }

  /**
   * Get SOP summaries by status
   * @param status Status to filter by
   * @returns Array of SOP summaries with the specified status
   */
  async getSummariesByStatus(status: string): Promise<SOPSummary[]> {
    try {
      const summariesCollection = collection(this.db, SOP_SUMMARIES_COLLECTION);
      const statusQuery = query(summariesCollection, where('status', '==', status));
      const snapshot = await firestoreWrapper.getDocs(statusQuery);
      return snapshot.docs.map(doc => doc.data() as SOPSummary);
    } catch (error) {
      console.error(`Error getting SOP summaries with status ${status}:`, error);
      throw new Error('Failed to get SOP summaries by status');
    }
  }

  /**
   * Get summary statistics
   * @returns Statistics about SOP summaries
   */
  async getSummaryStats(): Promise<SOPSummaryStats> {
    try {
      const summariesCollection = collection(this.db, SOP_SUMMARIES_COLLECTION);
      const snapshot = await firestoreWrapper.getDocs(summariesCollection);
      const summaries = snapshot.docs.map(doc => doc.data() as SOPSummary);
      
      const completed = summaries.filter(s => s.status === 'completed').length;
      const pending = summaries.filter(s => s.status === 'pending').length;
      const error = summaries.filter(s => s.status === 'error').length;
      const permissionDenied = summaries.filter(s => s.status === 'permission_denied').length;
      
      // Calculate averages for completed summaries
      const completedSummaries = summaries.filter(s => s.status === 'completed');
      
      let averageProcessingTime: number | undefined;
      let averageDocLength: number | undefined;
      
      if (completedSummaries.length > 0) {
        const processingTimes = completedSummaries
          .filter(s => s.processingTime !== undefined)
          .map(s => s.processingTime as number);
          
        const docLengths = completedSummaries
          .filter(s => s.docLength !== undefined)
          .map(s => s.docLength as number);
          
        if (processingTimes.length > 0) {
          averageProcessingTime = processingTimes.reduce((sum, time) => sum + time, 0) / processingTimes.length;
        }
        
        if (docLengths.length > 0) {
          averageDocLength = docLengths.reduce((sum, length) => sum + length, 0) / docLengths.length;
        }
      }
      
      return {
        total: summaries.length,
        completed,
        pending,
        error,
        permissionDenied,
        averageProcessingTime,
        averageDocLength
      };
    } catch (error) {
      console.error('Error getting SOP summary stats:', error);
      throw new Error('Failed to get SOP summary stats');
    }
  }

  /**
   * Enrich SOPs with summaries
   * @param sops Array of SOPs
   * @returns Array of SOPs with summaries
   */
  async enrichSOPsWithSummaries(sops: SOP[]): Promise<SOPWithSummary[]> {
    try {
      // Get all summaries
      const summaries = await this.getAllSummaries();
      const summaryMap = new Map<string, SOPSummary>();
      
      // Create a map of summaries by ID for quick lookup
      summaries.forEach(summary => {
        summaryMap.set(summary.id, summary);
      });
      
      // Enrich SOPs with summaries
      return sops.map(sop => {
        const summary = summaryMap.get(sop.id);
        
        return {
          ...sop,
          shortSummary: summary?.status === 'completed' ? summary.shortSummary : undefined,
          detailedSummary: summary?.status === 'completed' ? summary.detailedSummary : undefined,
          keywords: summary?.status === 'completed' ? summary.keywords : undefined,
          hasSummary: !!summary && summary.status === 'completed',
          summaryStatus: summary?.status,
          // Preserve flag-related fields
          flaggedForReview: sop.flaggedForReview,
          flagReason: sop.flagReason,
          flaggedBy: sop.flaggedBy,
          flaggedAt: sop.flaggedAt
        };
      });
    } catch (error) {
      console.error('Error enriching SOPs with summaries:', error);
      throw new Error('Failed to enrich SOPs with summaries');
    }
  }

  /**
   * Generate summaries for SOPs
   * @param params Parameters for generating summaries
   * @returns Result of the generation process
   */
  async generateSummaries(params: {
    batchSize?: number;
    startIndex?: number;
    testMode?: boolean;
    sopIds?: string[];
    onlyMissing?: boolean;
  }): Promise<{
    success: boolean;
    message: string;
    results?: {
      total: number;
      completed: number;
      failed: number;
      skipped: number;
      details: Array<{
        id: string;
        title: string;
        status: 'completed' | 'failed' | 'skipped';
        reason?: string;
        shortSummary?: string;
        detailedSummary?: string;
        keywords?: string[];
        error?: string;
      }>;
    };
    stats?: SOPSummaryStats;
  }> {
    try {
      const generateSOPSummaries = httpsCallable(this.functions, 'generateSOPSummaries');
      const result = await generateSOPSummaries(params);
      return result.data as any;
    } catch (error) {
      console.error('Error generating summaries:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }
}

export const sopSummaryService = SOPSummaryService.getInstance();
export default sopSummaryService;
